import { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Search, User, Building2, Mail, Phone, X, Plus } from "lucide-react";
import { API_URL, API_BASE_URL } from "../config/api";


/**
 * Contact/Company Autocomplete Component
 * Used across the application for quick-fill in forms
 * 
 * Props:
 * - type: "contact" | "company" | "both" (default: "both")
 * - onSelect: (item) => void - callback when item is selected
 * - value: string - current input value (controlled)
 * - onChange: (value) => void - callback when input changes
 * - placeholder: string
 * - className: string
 * - showAddNew: boolean - show "Add New" button
 * - onAddNew: () => void - callback when "Add New" is clicked
 */
export function ContactAutocomplete({
  type = "both",
  onSelect,
  value = "",
  onChange,
  placeholder = "Search contacts or companies...",
  className = "",
  showAddNew = false,
  onAddNew,
  disabled = false,
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState({ contacts: [], companies: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync external value
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const search = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults({ contacts: [], companies: [] });
      return;
    }

    setLoading(true);
    try {
      const promises = [];
      
      if (type === "contact" || type === "both") {
        promises.push(
          axios.get(`${API_URL}/crm/contacts/search`, {
            headers: getAuthHeaders(),
            params: { q: searchQuery, limit: 5 }
          })
        );
      } else {
        promises.push(Promise.resolve({ data: { contacts: [] } }));
      }
      
      if (type === "company" || type === "both") {
        promises.push(
          axios.get(`${API_URL}/crm/companies/search`, {
            headers: getAuthHeaders(),
            params: { q: searchQuery, limit: 5 }
          })
        );
      } else {
        promises.push(Promise.resolve({ data: { companies: [] } }));
      }

      const [contactsRes, companiesRes] = await Promise.all(promises);
      
      setResults({
        contacts: contactsRes.data.contacts || [],
        companies: companiesRes.data.companies || []
      });
      setIsOpen(true);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
    search(newValue);
  };

  const handleSelect = (item, itemType) => {
    setQuery(item.name || item.email || "");
    onChange?.(item.name || item.email || "");
    setIsOpen(false);
    onSelect?.({ ...item, _type: itemType });
  };

  const handleClear = () => {
    setQuery("");
    onChange?.("");
    setResults({ contacts: [], companies: [] });
    onSelect?.(null);
  };

  const hasResults = results.contacts.length > 0 || results.companies.length > 0;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          disabled={disabled}
          data-testid="crm-autocomplete-input"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && (hasResults || showAddNew) && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {/* Contacts Section */}
          {results.contacts.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Contacts
              </div>
              {results.contacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleSelect(contact, "contact")}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border/30 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{contact.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{contact.email}</span>
                        {contact.company_name && (
                          <>
                            <span>•</span>
                            <Building2 className="h-3 w-3" />
                            <span className="truncate">{contact.company_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Companies Section */}
          {results.companies.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Companies
              </div>
              {results.companies.map((company) => (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => handleSelect(company, "company")}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border/30 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{company.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {company.domain && `@${company.domain}`}
                        {company.country && ` • ${company.country}`}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!hasResults && query.length >= 2 && !loading && (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No results found for "{query}"
            </div>
          )}

          {/* Add New Button */}
          {showAddNew && (
            <button
              type="button"
              onClick={onAddNew}
              className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2 text-primary border-t border-border"
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">Add New Contact/Company</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for inline forms
 */
export function ContactQuickPicker({ onSelect, placeholder = "Type to search...", className = "" }) {
  return (
    <ContactAutocomplete
      type="both"
      onSelect={onSelect}
      placeholder={placeholder}
      className={className}
      showAddNew={false}
    />
  );
}

export default ContactAutocomplete;
